import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  CloudflareTurnstile,
  type CloudflareTurnstileHandle,
} from "@/components/auth/CloudflareTurnstile";
import { MfaVerifyDialog } from "@/components/auth/MfaVerifyDialog";
import { MfaSetupDialog } from "@/components/auth/MfaSetupDialog";
import {
  useAuthContext,
  SupportedAuthProvider,
} from "@/providers/AuthProvider";
import { authService } from "@/services";
import logo from "@/assets/images/logo.png";

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  turnstile?: string;
}

interface MfaState {
  challengeName: "SOFTWARE_TOKEN_MFA" | "MFA_SETUP" | null;
  session: string | null;
  email: string | null;
  secretCode: string | null;
}

export default function LoginPage() {
  const { loginWithOIDCProvider, completeEmailLogin } = useAuthContext();
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [mfaState, setMfaState] = useState<MfaState>({
    challengeName: null,
    session: null,
    email: null,
    secretCode: null,
  });
  const [mfaVerifyError, setMfaVerifyError] = useState<string | null>(null);
  const [mfaVerifyLoading, setMfaVerifyLoading] = useState(false);
  const turnstileRef = useRef<CloudflareTurnstileHandle>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";
    if (!turnstileToken && !isDevelopment) {
      newErrors.turnstile = "Please complete the security verification";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await authService.loginWithEmail({
        email: formData.email,
        password: formData.password,
        turnstileToken: turnstileToken!,
      });

      if (response.challengeName === "SOFTWARE_TOKEN_MFA") {
        setMfaState({
          challengeName: "SOFTWARE_TOKEN_MFA",
          session: response.session!,
          email: response.email!,
          secretCode: null,
        });
        return;
      }

      if (response.challengeName === "MFA_SETUP") {
        const setupResponse = await authService.setupMfa({
          session: response.session!,
        });
        setMfaState({
          challengeName: "MFA_SETUP",
          session: setupResponse.session,
          email: response.email!,
          secretCode: setupResponse.secretCode,
        });
        return;
      }

      // No MFA required, complete login
      if (response.accessToken && response.idToken) {
        completeEmailLogin({
          accessToken: response.accessToken,
          idToken: response.idToken,
          refreshToken: response.refreshToken!,
          expiresIn: response.expiresIn!,
        });
        navigate("/");
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to sign in",
      );
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (code: string) => {
    if (!mfaState.session || !mfaState.email) return;

    setMfaVerifyLoading(true);
    setMfaVerifyError(null);

    try {
      const response = await authService.respondToMfaChallenge({
        session: mfaState.session,
        code,
        email: mfaState.email,
      });

      completeEmailLogin({
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      });
      setMfaState({
        challengeName: null,
        session: null,
        email: null,
        secretCode: null,
      });
      navigate("/");
    } catch (error) {
      setMfaVerifyError(
        error instanceof Error ? error.message : "Verification failed",
      );
    } finally {
      setMfaVerifyLoading(false);
    }
  };

  const handleMfaSetupVerify = async (
    code: string,
  ): Promise<string[] | null> => {
    if (!mfaState.session || !mfaState.email) return null;

    const response = await authService.verifyMfaSetup({
      session: mfaState.session,
      code,
      email: mfaState.email,
    });

    return response.backupCodes;
  };

  const handleMfaSetupComplete = () => {
    // After MFA setup is complete, user needs to login again
    setMfaState({
      challengeName: null,
      session: null,
      email: null,
      secretCode: null,
    });
    setSubmitError(null);
    // Clear form and show success message
    setFormData({ email: "", password: "" });
  };

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    if (errors.turnstile) {
      setErrors((prev) => ({ ...prev, turnstile: undefined }));
    }
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setErrors((prev) => ({
      ...prev,
      turnstile: "Verification failed. Please try again.",
    }));
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-8">
              <img src={logo} alt="EchoPeak Labs" className="w-16 h-16" />
            </Link>
            <h1 className="font-display text-3xl font-bold mb-2">
              Welcome Back
            </h1>
            <p className="text-muted-foreground">
              Sign in to access your dashboard
            </p>
          </div>

          <Card className="glass-card border-gradient">
            <CardContent className="p-6 space-y-4">
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() =>
                  loginWithOIDCProvider(SupportedAuthProvider.GOOGLE)
                }
              >
                <GoogleIcon />
                <span className="ml-3">Login with Google</span>
              </Button>

              <AnimatePresence mode="wait">
                {!showEmailForm ? (
                  <motion.div
                    key="email-button"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Button
                      variant="outline"
                      className="w-full h-12"
                      onClick={() => setShowEmailForm(true)}
                    >
                      <Mail className="h-5 w-5" />
                      <span className="ml-3">Login with Email</span>
                    </Button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="email-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-4 pt-2"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          Email login
                        </span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          className={
                            errors.password
                              ? "border-destructive pr-10"
                              : "pr-10"
                          }
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.password}
                        </p>
                      )}
                    </div>

                    <div className="pt-2">
                      <CloudflareTurnstile
                        ref={turnstileRef}
                        onVerify={handleTurnstileVerify}
                        onError={handleTurnstileError}
                        onExpire={handleTurnstileExpire}
                        theme="dark"
                      />
                      {errors.turnstile && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.turnstile}
                        </p>
                      )}
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                        <p className="text-sm text-destructive">
                          {submitError}
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign in
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => setShowEmailForm(false)}
                    >
                      Back to options
                    </Button>
                  </motion.form>
                )}
              </AnimatePresence>

              <p className="text-xs text-center text-muted-foreground pt-2">
                By signing in, you agree to our{" "}
                <Link
                  to="/legal/terms"
                  className="text-primary hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/legal/privacy"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* MFA Verification Dialog */}
      <MfaVerifyDialog
        open={mfaState.challengeName === "SOFTWARE_TOKEN_MFA"}
        onOpenChange={(open) => {
          if (!open) {
            setMfaState({
              challengeName: null,
              session: null,
              email: null,
              secretCode: null,
            });
            setMfaVerifyError(null);
          }
        }}
        onVerify={handleMfaVerify}
        isLoading={mfaVerifyLoading}
        error={mfaVerifyError}
      />

      {/* MFA Setup Dialog */}
      {mfaState.challengeName === "MFA_SETUP" &&
        mfaState.secretCode &&
        mfaState.email && (
          <MfaSetupDialog
            open={true}
            onOpenChange={() => {}}
            secretCode={mfaState.secretCode}
            email={mfaState.email}
            onVerify={handleMfaSetupVerify}
            onComplete={handleMfaSetupComplete}
          />
        )}
    </div>
  );
}
